/**
 * "Devices Intune holds for <name>": a read-only panel shown on the offboarding form once a leaver is picked and
 * on the offboarding request view (Phase 2c). Reporting, not form logic (Marjorie, 2026-10-15): nothing is
 * pre-ticked from it, nothing from it is stored, and a failed or empty lookup never blocks anything.
 * One debounced GET /api/directory/users/{id|upn}/devices per pick; a re-pick cancels the previous lookup.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import { ApiError, getUserDevices, type ManagedDevice } from './api';
import type { App } from './app';
import { instant } from './format';

export type DevicesStatus = 'idle' | 'loading' | 'ok' | 'none' | 'not-found' | 'unavailable';

export interface DevicesState {
  /** The directory key being looked up (object id, else UPN); '' when nobody is picked. */
  key: string;
  name: string;
  status: DevicesStatus;
  rows: ManagedDevice[];
  seq: number;
  timer?: number;
}

export const newDevices = (): DevicesState => ({ key: '', name: '', status: 'idle', rows: [], seq: 0 });

export function clearDevices(st: DevicesState): void {
  window.clearTimeout(st.timer);
  st.seq++;
  Object.assign(st, { key: '', name: '', status: 'idle', rows: [] });
}

/** Look the person up (debounced; a newer call supersedes an older one). `name` is only for the panel's heading. */
export function lookupDevices(app: App, st: DevicesState, key: string, name: string, debounceMs = 300): void {
  window.clearTimeout(st.timer);
  const seq = ++st.seq;
  Object.assign(st, { key, name, status: 'loading', rows: [] });
  st.timer = window.setTimeout(async () => {
    try {
      const r = await getUserDevices(key);
      if (seq !== st.seq) return;
      st.rows = r.rows;
      st.status = r.rows.length ? 'ok' : 'none';
    } catch (e) {
      if (seq !== st.seq) return;
      st.rows = [];
      st.status = e instanceof ApiError && e.status === 404 ? 'not-found' : 'unavailable';
    }
    app.redraw();
  }, debounceMs);
}

const os = (d: ManagedDevice) => [d.operatingSystem, d.osVersion].filter(Boolean).join(' ');
const model = (d: ManagedDevice) => [d.manufacturer, d.model].filter(Boolean).join(' ');

export function devicesPanel(st: DevicesState): TemplateResult {
  if (st.status === 'idle') return html``;
  const heading = html`<h4>Devices Intune holds for ${st.name}</h4>`;
  let body: TemplateResult;
  switch (st.status) {
    case 'loading':
      body = html`<p class="muted">Looking up devices…</p>`;
      break;
    case 'none':
    case 'not-found':
      body = html`<p class="muted">No Intune devices found for ${st.name}</p>`;
      break;
    case 'unavailable':
      body = html`<p class="muted">Device lookup unavailable</p>`;
      break;
    default:
      body = html`<table class="devices">
        <thead><tr><th>Device</th><th>Model</th><th>OS</th><th>Last sync</th></tr></thead>
        <tbody>
          ${st.rows.map(
            (d) => html`<tr>
              <td>${d.deviceName || html`<span class="none">—</span>`}${d.serialNumber ? html`<span class="muted sn">S/N ${d.serialNumber}</span>` : nothing}</td>
              <td>${model(d) || html`<span class="none">—</span>`}</td>
              <td>${os(d) || html`<span class="none">—</span>`}</td>
              <td>${d.lastSyncDateTime ? instant(d.lastSyncDateTime) : html`<span class="none">—</span>`}</td>
            </tr>`,
          )}
        </tbody>
      </table>
      <p class="hint">${st.rows.length} device${st.rows.length === 1 ? '' : 's'} with ${st.name} as the Intune primary user. For information only: tick the equipment to collect yourself.</p>`;
  }
  return html`<div class="facet devices-panel" aria-live="polite">${heading}${body}</div>`;
}
